// React imports
import React, { useEffect, useState, useCallback, useMemo } from "react";

// Material UI imports
import { 
  MenuItem, 
  Box, 
  FormControlLabel, 
  Slider, 
  FormControl, 
  IconButton
} from "@mui/material";
import { Add } from "@mui/icons-material";

// Third-party imports
import { enqueueSnackbar } from "notistack";

// ERP Common imports
import Checkbox from "@/components/checkbox/checkbox";
import Select from "@/components/select/select";
import TextField from "@/components/text-field/text-field";
import Typography from "@/components/typography/typography";
import Button from "@/components/button/button";
import { Trash } from "@/components/icons";

// Local imports
import FieldSelect from "./field-select";
import "../custom-form.scss";

interface Option {
  label: string;
  value: string;
  [key: string]: string;
}

interface ElementJSON {
  id: string;
  field_type: string;
  label: string;
  placeholder: string;
  default_value: string | null;
  is_multiselect: boolean;
  is_required: boolean;
  is_unique: boolean;
  is_fullwidth: boolean;
  custom_width: number;
  type?: string;
  is_new?: boolean;
  options: Option[];
}

interface SelectEditFormProps {
  element: ElementJSON;
  handlePropertyValueUpdate: (
    event: React.ChangeEvent<HTMLInputElement> | null,
    name?: string | null,
    value?: any,
    fieldType?: string
  ) => void;
  handleSelectOptionValueUpdate: (options: Option[]) => void;
  handleColumnSelect: (event: React.ChangeEvent<{ value: unknown }>) => void;
  handleWidthChange: (value: number | number[]) => void;
}

const COLUMN_PER_ROW_OPTIONS = [1, 2, 3, 4, 5, 6] as const;

export const SelectEditForm: React.FC<SelectEditFormProps> = React.memo(({
  element,
  handlePropertyValueUpdate,
  handleSelectOptionValueUpdate,
  handleColumnSelect,
  handleWidthChange
}) => {
  // Local state management
  const [selectOptions, setSelectOptions] = useState<Option[]>(element.options || []);
  const [newOption, setNewOption] = useState<Option>({ label: "", value: "" });
  const [isAddingOption, setIsAddingOption] = useState(false);

  // Sync options with parent element when element changes
  useEffect(() => {
    if (JSON.stringify(selectOptions) !== JSON.stringify(element.options || [])) {
      setSelectOptions(element.options || []);
    }
  }, [element.options, selectOptions]);

  // Memoized computed values
  const isFieldDisabled = useMemo(() => 
    element.type === "system" || !element.is_new, 
    [element.type, element.is_new]
  );

  const selectedOptionLabel = useMemo(() => {
    const selectedOption = selectOptions.find(option => option.value === element.default_value);
    return selectedOption?.label || "Select Default";
  }, [selectOptions, element.default_value]);

  // Optimized event handlers with useCallback
  const handleFieldUpdate = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    handlePropertyValueUpdate(null, null, null, e.target.value);
  }, [handlePropertyValueUpdate]);

  const handleSelectUpdate = useCallback((e: React.ChangeEvent<{ value: unknown }>) => {
    handlePropertyValueUpdate(null, null, e.target.value);
  }, [handlePropertyValueUpdate]);

  const handleMultiSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = e.target.checked;
    // Reset default value when enabling multiselect
    const defaultValue = isChecked ? null : element.default_value;
    
    // Create a synthetic event for the property update
    const syntheticEvent = {
      target: {
        name: 'is_multiselect',
        checked: isChecked,
        value: isChecked
      }
    } as React.ChangeEvent<HTMLInputElement>;
    
    handlePropertyValueUpdate(syntheticEvent);
    
    // If enabling multiselect, also clear the default value
    if (isChecked && element.default_value) {
      handlePropertyValueUpdate(null, 'default_value', null);
    }
  }, [handlePropertyValueUpdate, element.default_value]);

  const handleNewOptionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setNewOption(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleAddOption = useCallback(() => {
    if (!newOption.label.trim() || !newOption.value.trim()) {
      return; // Don't add empty options
    }

    // Check for duplicate values
    const isDuplicate = selectOptions.some(option => 
      option.value === newOption.value || option.label === newOption.label
    );
    
    if (isDuplicate) {
      // Could show an error message here
      enqueueSnackbar("Option with this value or label already exists.", { variant: "error" });
      return;
    }

    const updatedOptions = [...selectOptions, { ...newOption }];
    setSelectOptions(updatedOptions);
    handleSelectOptionValueUpdate(updatedOptions);
    
    // Reset form
    setNewOption({ label: "", value: "" });
    setIsAddingOption(false);
  }, [newOption, selectOptions, handleSelectOptionValueUpdate]);

  const handleRemoveOption = useCallback((indexToRemove: number) => {
    const updatedOptions = selectOptions.filter((_, index) => index !== indexToRemove);
    setSelectOptions(updatedOptions);
    handleSelectOptionValueUpdate(updatedOptions);
    
    // Clear default value if the removed option was selected
    const removedOption = selectOptions[indexToRemove];
    if (element.default_value === removedOption?.value) {
      handlePropertyValueUpdate(null, 'default_value', null);
    }
  }, [selectOptions, handleSelectOptionValueUpdate, element.default_value, handlePropertyValueUpdate]);

  const handleWidthChangeCallback = useCallback((
    _: Event, 
    newValue: number | number[]
  ) => {
    handleWidthChange(newValue);
  }, [handleWidthChange]);

  return (
    <Box className="formSection--FieldContainer">
      {/* Field Type Selection */}
      <Box>
        <FieldSelect
          variant="outlined"
          onChange={handleFieldUpdate}
          defaultValue={element.field_type}
          label="Field Type"
          disabled={isFieldDisabled}
        />
      </Box>

      {/* Label Field */}
      <Box>
        <TextField
          fullWidth
          label="Label"
          is_required
          name="label"
          type="text"
          onChange={handlePropertyValueUpdate}
          defaultValue={element.label}
        />
      </Box>

      {/* Placeholder Field */}
      <Box>
        <TextField
          fullWidth
          label="Placeholder"
          is_required
          name="placeholder"
          type="text"
          onChange={handlePropertyValueUpdate}
          defaultValue={element.placeholder}
        />
      </Box>

      {/* Default Value Selection */}
      <Box>
        <Select
          fullWidth
          placeholder="Select Default"
          size="small"
          name="default_value"
          onChange={handleSelectUpdate}
          value={element.default_value || ""}
          label="Default"
          variant="outlined"
          disabled={element.is_multiselect}
          renderValue={() => selectedOptionLabel}
        >
          {selectOptions.map((option, index) => (
            <MenuItem key={`${option.value}-${index}`} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </Box>

      {/* Column Per Row Selection */}
      <Box>
        <FormControl fullWidth>
          <Select
            fullWidth
            placeholder="Select Column Per Row"
            size="small"
            onChange={handleColumnSelect}
            label="Select Column Per Row"
            name="columnPerRow"
            variant="outlined"
          >
            {COLUMN_PER_ROW_OPTIONS.map((option) => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Width Slider */}
      <Box>
        <Typography sx={{ color: 'black', mb: 1 }}>
          Adjust Width:
        </Typography>
        <Box pr={2}>
          <Slider
            value={element.custom_width || 6}
            min={1}
            max={12}
            step={1}
            onChange={handleWidthChangeCallback}
            aria-labelledby="width-slider"
            sx={{ color: "#4AC08C" }}
            marks
            valueLabelDisplay="auto"
          />
        </Box>
      </Box>

      {/* Options Management */}
      <Box className="formSection--SelectOption">
        <Typography 
          type="s5" 
          weight="medium" 
          color="theme.secondary.800"
          sx={{ mb: 0.5 }}
        >
          Options
        </Typography>
        
        {/* Existing Options */}
        {selectOptions.map((option, index) => (
          <Box key={`option-${index}`} sx={{ mb: 1, display: 'flex', gap: 1 }}>
            <TextField
              fullWidth
              value={`${option.label} (${option.value})`}
              InputProps={{ readOnly: true }}
            />
            <IconButton
              color="error"
              size="small"
              onClick={() => handleRemoveOption(index)}
            >
              <Trash />
            </IconButton>
          </Box>
        ))}

        {/* Add New Option Form */}
        {isAddingOption && (
          <Box className="formSection--SelectOption" sx={{ mt:0.5, p: 2, border: '1px solid #ddd', borderRadius: 1 }}>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Option Label"
                is_required
                name="label"
                type="text"
                placeholder="Enter option label"
                value={newOption.label}
                onChange={handleNewOptionChange}
              />
            </Box>
            <Box sx={{ mb: 2 }}>
              <TextField
                fullWidth
                label="Option Value"
                placeholder="Enter option value"
                is_required
                name="value"
                type="text"
                value={newOption.value}
                onChange={handleNewOptionChange}
              />
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                onClick={handleAddOption}
                // className="formSection--Button"
                startIcon={<Add />}
                disabled={!newOption.label.trim() || !newOption.value.trim()}
              >
                Add Option
              </Button>
              <Button
                onClick={() => {
                  setIsAddingOption(false);
                  setNewOption({ label: "", value: "" });
                }}
                variant="outlined"
              >
                Cancel
              </Button>
            </Box>
          </Box>
        )}

        {/* Add Option Button */}
        {!isAddingOption && (
          <Button
            onClick={() => setIsAddingOption(true)}
            variant="outlined"
            className="formSection--Button"
            startIcon={<Add />}
            sx={{ mt: 1 }}
          >
            Add Option
          </Button>
        )}
      </Box>

      {/* Form Controls */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <FormControlLabel
          name="is_multiselect"
          control={<Checkbox />}
          onChange={handleMultiSelect}
          checked={element.is_multiselect}
          label="Enable Multi Select"
        />

        <FormControlLabel
          name="is_required"
          control={<Checkbox />}
          onChange={handlePropertyValueUpdate}
          checked={element.is_required}
          label="This field is required"
        />

        <FormControlLabel
          name="is_unique"
          control={<Checkbox />}
          onChange={handlePropertyValueUpdate}
          checked={element.is_unique}
          label="Is Unique"
        />

        <FormControlLabel
          name="is_fullwidth"
          control={<Checkbox />}
          onChange={handlePropertyValueUpdate}
          checked={element.is_fullwidth}
          label="Full Width"
        />
      </Box>
    </Box>
  );
});

SelectEditForm.displayName = 'SelectEditForm';

export default SelectEditForm;